import type { ParamValues } from '../types';
import { spanToMillimetres } from '../params';
import { TAU, clamp01, lerp, smoothstep, triangleWave } from '../math/scalar';
import {
  PROFILE_OPTIONS,
  type WaveProfile,
  axisDistance,
  crossAxisDistance,
  pitchAlongAxis,
  waveProfile,
} from './helpers';
import { definePattern } from './registry';

/**
 * Travelling-wave patterns.
 *
 * All three read their position from wall space, so a wave simply keeps going
 * when it crosses a panel joint. Their wavelengths are declared in *panels*
 * rather than millimetres, which is what makes a motif still line up with the
 * joints after the panel size is changed.
 */

interface WavesParams extends ParamValues {
  profile: WaveProfile;
  angle: number;
  wavelength: number;
  phase: number;
  harmonic: number;
  meander: number;
  meanderSpan: number;
  duty: number;
}

export const wavesPattern = definePattern<WavesParams>({
  id: 'waves',
  name: 'Waves',
  description:
    'Straight travelling waves that run across the whole wall. The wavelength is measured in panels, so the crests keep meeting the panel joints at the same place.',
  category: 'wave',
  tags: ['linear', 'seamless', 'classic'],
  params: [
    {
      kind: 'select',
      key: 'profile',
      label: 'Profile',
      options: PROFILE_OPTIONS,
      default: 'sine',
      group: 'Shape',
    },
    {
      kind: 'angle',
      key: 'angle',
      label: 'Direction',
      default: 0,
      help: 'Direction the wave travels in.',
      group: 'Shape',
    },
    {
      kind: 'span',
      key: 'wavelength',
      label: 'Wavelength',
      min: 0.125,
      max: 8,
      step: 0.125,
      default: 1,
      help: 'Crest-to-crest distance, measured in panels.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'phase',
      label: 'Phase',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'harmonic',
      label: 'Harmonic',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      help: 'Blends in a second wave at double the frequency.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'meander',
      label: 'Meander',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      help: 'Bends the crests sideways instead of keeping them straight.',
      group: 'Detail',
    },
    {
      kind: 'span',
      key: 'meanderSpan',
      label: 'Meander span',
      min: 0.25,
      max: 12,
      step: 0.25,
      default: 2,
      help: 'How far one full bend reaches, in panels.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'duty',
      label: 'Plateau width',
      min: 0.05,
      max: 0.95,
      step: 0.01,
      default: 0.5,
      help: 'Only used by the step profile.',
      group: 'Detail',
    },
  ],
  sample(sample, params) {
    const { wall, x, y } = sample;
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const wavelength = spanToMillimetres(params.wavelength, pitch);

    let distance = axisDistance(x, y, params.angle);

    if (params.meander > 0) {
      const meanderLength = spanToMillimetres(params.meanderSpan, pitch);
      const cross = crossAxisDistance(x, y, params.angle);
      distance += Math.sin((cross / meanderLength) * TAU) * params.meander * wavelength * 0.5;
    }

    const phase = distance / wavelength + params.phase;
    const primary = waveProfile(phase, params.profile, params.duty);

    if (params.harmonic <= 0) return primary;

    const secondary = waveProfile(phase * 2, params.profile, params.duty);
    return clamp01((primary + secondary * params.harmonic) / (1 + params.harmonic));
  },
});

interface RippleParams extends ParamValues {
  profile: WaveProfile;
  centerX: number;
  centerY: number;
  wavelength: number;
  phase: number;
  falloff: number;
  aspect: number;
  duty: number;
}

export const ripplePattern = definePattern<RippleParams>({
  id: 'ripple',
  name: 'Ripple',
  description:
    'Concentric rings radiating from a point on the wall. The centre is given in wall fractions, so it stays put when the grid grows.',
  category: 'wave',
  tags: ['radial', 'seamless', 'focal'],
  params: [
    {
      kind: 'select',
      key: 'profile',
      label: 'Profile',
      options: PROFILE_OPTIONS,
      default: 'sine',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'centerX',
      label: 'Centre X',
      min: -0.5,
      max: 1.5,
      step: 0.01,
      default: 0.5,
      help: 'Fraction of the wall width; values outside 0..1 sit off the wall.',
      group: 'Placement',
    },
    {
      kind: 'number',
      key: 'centerY',
      label: 'Centre Y',
      min: -0.5,
      max: 1.5,
      step: 0.01,
      default: 0.5,
      group: 'Placement',
    },
    {
      kind: 'span',
      key: 'wavelength',
      label: 'Ring spacing',
      min: 0.1,
      max: 6,
      step: 0.05,
      default: 0.5,
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'phase',
      label: 'Phase',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'falloff',
      label: 'Falloff',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      help: 'Fades the rings out towards the wall edges.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'aspect',
      label: 'Aspect',
      min: 0.25,
      max: 4,
      step: 0.05,
      default: 1,
      help: 'Stretches the rings into ellipses.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'duty',
      label: 'Plateau width',
      min: 0.05,
      max: 0.95,
      step: 0.01,
      default: 0.5,
      group: 'Detail',
    },
  ],
  sample(sample, params) {
    const { wall, x, y } = sample;
    const cx = params.centerX * wall.width;
    const cy = params.centerY * wall.height;
    const dx = (x - cx) / params.aspect;
    const dy = y - cy;
    const radius = Math.sqrt(dx * dx + dy * dy);

    const pitch = (wall.pitchX + wall.pitchY) * 0.5;
    const wavelength = spanToMillimetres(params.wavelength, pitch);
    const height = waveProfile(radius / wavelength + params.phase, params.profile, params.duty);

    if (params.falloff <= 0) return height;

    const reach = Math.hypot(wall.width, wall.height) * 0.5;
    const fade = 1 - smoothstep(0, reach, radius) * params.falloff;
    return clamp01(lerp(0, height, fade));
  },
});

interface ChevronParams extends ParamValues {
  profile: WaveProfile;
  angle: number;
  wavelength: number;
  zigSpan: number;
  amplitude: number;
  phase: number;
  duty: number;
}

export const chevronPattern = definePattern<ChevronParams>({
  id: 'chevron',
  name: 'Chevron',
  description:
    'Waves whose crests zigzag, producing the classic herringbone V. The zigzag period is measured in panels so the V tips land on the joints.',
  category: 'wave',
  tags: ['zigzag', 'herringbone', 'seamless'],
  params: [
    {
      kind: 'select',
      key: 'profile',
      label: 'Profile',
      options: PROFILE_OPTIONS,
      default: 'triangle',
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Direction', default: 0, group: 'Shape' },
    {
      kind: 'span',
      key: 'wavelength',
      label: 'Wavelength',
      min: 0.125,
      max: 4,
      step: 0.125,
      default: 0.5,
      group: 'Shape',
    },
    {
      kind: 'span',
      key: 'zigSpan',
      label: 'Zigzag span',
      min: 0.25,
      max: 8,
      step: 0.25,
      default: 1,
      help: 'Distance between two V tips, in panels.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'amplitude',
      label: 'Zigzag depth',
      min: 0,
      max: 4,
      step: 0.05,
      default: 1,
      help: 'How far the crests shift, in wavelengths.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'phase',
      label: 'Phase',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'duty',
      label: 'Plateau width',
      min: 0.05,
      max: 0.95,
      step: 0.01,
      default: 0.5,
      group: 'Detail',
    },
  ],
  sample(sample, params) {
    const { wall, x, y } = sample;
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const wavelength = spanToMillimetres(params.wavelength, pitch);
    const zigLength = spanToMillimetres(params.zigSpan, pitch);

    const cross = crossAxisDistance(x, y, params.angle);
    // A triangle wave on the cross axis shifts the crest back and forth
    // linearly, which is exactly what turns straight waves into chevrons.
    const zig = triangleWave(cross, zigLength);
    const offset = (zig - 0.5) * 2 * params.amplitude * wavelength;

    const distance = axisDistance(x, y, params.angle) + offset;
    return waveProfile(distance / wavelength + params.phase, params.profile, params.duty);
  },
});
